import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Phone, Sparkles, Shield, Award, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchCollection } from '../../lib/cms';
import Beregner from '../../components/Beregner';
import GalleryModal from '../../components/GalleryModal';
import SEO from '../../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function HomeEn() {
  const [testimonials, setTestimonials] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [loadVideo, setLoadVideo] = useState(true);

  useEffect(() => {
    async function loadData() {
      const testData = await fetchCollection('testimonials', 'en');
      setTestimonials(testData);
      const galData = await fetchCollection('galleri', 'en');
      if (galData && galData[0]?.images) {
        setGalleryImages(galData[0].images);
      }
    }
    loadData();
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCount, maxIndex, currentIndex]);

  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isPaused, maxIndex]);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Danshaker',
    description: 'Danshaker is the original Danish-developed sorting machine for horse stables, halving bedding consumption and manual strain.',
    image: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/14d0f8cb-56b7-4a51-8379-6cfdf86af873.png',
    telephone: '+45 25 30 35 89',
    email: 'info@danshaker.dk',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Tebbestrupvej 72',
      addressLocality: 'Randers SV',
      postalCode: '8940',
      addressCountry: 'DK',
    },
    url: 'https://danshaker.dk/en',
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Halve Your Stable Work"
        description="Danshaker is the original Danish sorting machine for horse stables. Save up to 50% time, halve bedding costs, and protect your physical health. Book a free demo today."
        path="/en"
        lang="en"
        schema={homeSchema}
      />

      {/* Decorative background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />
      <div className="pointer-events-none absolute top-[30%] left-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[10%] right-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />

      {/* Hero Section */}
      <section className="relative mx-auto max-w-7xl px-4 pt-8 pb-10 md:px-8 md:pt-12 lg:pt-16 md:pb-14 lg:pb-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Content */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-8 lg:col-span-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2.5 rounded-full border border-[#0E67A2]/20 bg-[#0E67A2]/5 px-5 py-2 text-sm font-bold text-[#0E67A2]"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span>The original Danish stable sorting machine</span>
            </motion.div>

            <div className="space-y-4">
              <motion.h1 variants={fadeInUp} className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-7xl leading-none">
                Halve the work<br />
                <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">in the stable!</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="max-w-2xl text-lg md:text-xl text-slate-800 leading-relaxed font-medium">
                Danshaker is engineered to eliminate heavy, repetitive mucking out work. Save up to 50% of your time, halve your bedding consumption, and protect your physical wellbeing.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/en/free-demo"
                className="flex items-center gap-2.5 rounded-xl bg-[#0E67A2] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-[#0E67A2]/10 transition duration-300 hover:scale-[1.02] hover:bg-[#0E67A2]/90"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" />
                Book free demo
              </Link>
              <Link
                to="/en/product"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                View product
              </Link>
              <Link
                to="/en/contact"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                Contact us
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-slate-200 pt-10 text-slate-500">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Danish design & craft</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Premium materials</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Prompt delivery</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image/Visual */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="relative lg:col-span-6"
          >
            <div className="relative mx-auto max-w-[750px] lg:max-w-none">
              <div className="absolute -inset-6 rounded-3xl bg-[#0E67A2]/5 blur-3xl" />

              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-xl">
                {loadVideo && (
                  <video
                    src="https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/dd23b170-9451-48c1-be2e-929bf23525fb.mov"
                    poster="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg"
                    className="w-full h-auto max-h-[600px] mx-auto rounded-2xl shadow-2xl bg-black"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  />
                )}
                <div className="absolute bottom-6 right-6 rounded-xl bg-white/95 px-4 py-2 text-xs font-bold text-slate-900 shadow-lg border border-slate-200">
                  See Danshaker in action
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Propositions (Time, Bedding, Body) */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-black text-slate-950 md:text-4xl">Why choose a Danshaker?</h2>
            <p className="mt-4 text-base text-slate-900 font-semibold">
              Danshaker elevates your stable working environment and dramatically reduces operational costs.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Save Time */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/a792de19-260d-4ca8-9514-ca552cf7c662.png"
                  alt="Save Time icon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Save TIME</h3>
              <p className="text-[#0E67A2] font-black mb-4">Up to 50% faster sorting</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Danshaker has cut daily stall cleaning time for several customers by over 50%. Free up more hours for your horses.
              </p>
            </motion.div>

            {/* Save Bedding */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/feb939a4-8f35-4c2d-a66d-e8556b06f45a.png"
                  alt="Save Bedding icon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Save BEDDING</h3>
              <p className="text-[#0E67A2] font-black mb-4">Up to 50% less waste</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Clean the bedding in seconds and reuse the clean material. Dispose of manure only and significantly reduce your expenses.
              </p>
            </motion.div>

            {/* Protect Body */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg md:col-span-2 lg:col-span-1">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/17efd1be-1500-4bfa-a02d-0792317ba244.png"
                  alt="Protect Body icon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Protect YOUR BODY</h3>
              <p className="text-[#0E67A2] font-black mb-4">Eliminate physical strain</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Avoid repetitive, jarring wrist, elbow, shoulder, and back movements. The machine performs the vigorous shaking for you.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">How much can you save?</h2>
            <p className="mt-4 text-slate-700 font-medium">
              Use our interactive savings calculator to see how fast a Danshaker pays for itself in your stable.
            </p>
          </div>

          <Beregner lang="en" />
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">What our customers say</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Read authentic feedback from professional facilities and private horse owners who use the Danshaker every day.
              </p>
            </div>

            <div 
              className="relative overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div 
                className="flex flex-nowrap transition-transform duration-500 ease-in-out gap-6"
                style={{ 
                  transform: `translateX(calc(-${currentIndex} * (100% + 1.5rem) / ${visibleCount}))` 
                }}
              >
                {testimonials.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex flex-col justify-between rounded-2xl border border-slate-300 bg-white p-6 shadow-md transition hover:border-[#0E67A2]/20 w-full shrink-0 md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        {item.image && item.image[0] ? (
                          <img
                            src={item.image[0]}
                            alt={item.name}
                            width="48"
                            height="48"
                            loading="lazy"
                            decoding="async"
                            className="h-12 w-12 rounded-full object-cover border border-slate-300"
                          />
                        ) : (
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-extrabold text-lg border border-[#0E67A2]/20">
                            {item.name ? item.name.charAt(0) : 'K'}
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-slate-900 text-base">{item.name}</h3>
                          <p className="text-xs text-slate-700 font-semibold">{item.company}</p>
                          <div className="flex items-center gap-1 text-amber-400 text-xs mt-0.5" aria-label="5 out of 5 stars">
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                          </div>
                        </div>
                      </div>
                      <div
                        className="text-sm text-slate-800 leading-relaxed italic font-medium"
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {maxIndex > 0 && (
              <div className="flex items-center justify-center gap-6 mt-10">
                <button
                  onClick={prevSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                        idx === currentIndex ? 'w-8 bg-[#0E67A2]' : 'w-2.5 bg-slate-200 hover:bg-slate-300'
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}

            <div className="mt-12 text-center">
              <Link
                to="/en/reviews"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 font-bold text-[#0E67A2] shadow-sm transition hover:bg-slate-50"
              >
                Read all reviews
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Experience Danshaker up close</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Click on the photos to view high-resolution details of the machine and its accessories.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => openGallery(idx)}
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:border-[#0E67A2]/30"
                >
                  <img
                    src={img}
                    alt={`Danshaker detail ${idx + 1}`}
                    width="300"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-slate-950/20 opacity-0 transition group-hover:opacity-100 flex items-center justify-center">
                    <span className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#0E67A2] border border-slate-200 shadow-md">Enlarge</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="border-t border-slate-300 bg-gradient-to-b from-white to-slate-100 py-12">
        <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
          <h2 className="text-3xl font-extrabold text-slate-900 md:text-5xl leading-tight">
            Ready to make everyday life<br />
            <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">in the stable easier?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-800 font-medium">
            Book a completely free and non-binding demo in your own stable. We show you exactly how the machine operates, and you get to test it yourself.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/en/free-demo"
              className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white shadow-lg shadow-[#0E67A2]/20 transition hover:bg-[#0E67A2]/90"
            >
              <Calendar className="h-5 w-5" aria-hidden="true" />
              Book free demo now
            </Link>
            <Link
              to="/en/contact"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-8 py-4 font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Contact us for guidance
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        images={galleryImages}
        currentIndex={galleryIndex}
        onClose={() => setIsGalleryOpen(false)}
        onPrev={() => setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
        onNext={() => setGalleryIndex((prev) => (prev + 1) % galleryImages.length)}
      />
    </div>
  );
}
